import { C, problems as problemList, reviewers, SLIDERS, type ProblemId, type ReviewInput, type Reviewer, type SliderId } from "@bd/content";
import { yearOf } from "./calendar";
import { idealFocus, type Compiled, type FitDetail, type ValueDetail } from "./design";
import { BALANCE } from "./balance";
import { hashString } from "./rng";
import type { BreakdownLine, Design, Review } from "./state";
import { clamp, money } from "./util";

export const BASE_WEIGHTS: Record<ReviewInput, number> = { fit: 0.3, focus: 0.25, polish: 0.2, value: 0.15, trend: 0.1 };

export interface TrendDetail {
  themeHeat: number; // 0..1
  mechHeat: number; // 0..1
  saturation: number; // 0..0.5, penalty
  copies: number; // same mechanic x theme in the last 3 years
  novel: boolean; // first time this combo has ever been released
  T: number;
}

export function trendScore(themeHeat: number, mechHeat: number, saturation: number, copies: number, novel: boolean): TrendDetail {
  const T = clamp(0.42 + 0.35 * themeHeat + 0.2 * mechHeat - saturation - Math.min(0.4, 0.08 * copies) + (novel ? 0.1 : 0));
  return { themeHeat, mechHeat, saturation, copies, novel, T };
}

export interface ReviewContext {
  design: Design;
  compiled: Compiled;
  fit: FitDetail;
  focusSim: number; // 0..1
  designQuality: number; // 0..1
  problems: Record<ProblemId, number>; // remaining true severities
  value: ValueDetail;
  trend: TrendDetail;
  hype: number; // 0..1
  artQuality: number;
  writingQuality: number;
  week: number;
  /** an expansion, edition, spin-off or sequel rather than a new game */
  followUp?: boolean;
}

export interface ReviewSet {
  reviews: Review[];
  score: number; // average, one decimal
  quality: number; // average pre-hype score / 10
  inputs: { F: number; M: number; P: number; V: number; T: number; H: number };
}

export function activeReviewers(d: Design, week: number): Reviewer[] {
  const year = yearOf(week);
  return reviewers.filter((r) => {
    if (year < r.year) return false;
    if (r.id === "dungeon_ledger") return d.format === "rpg_book" || d.audience === "roleplayers" || d.focus.immersion >= 3;
    return true;
  });
}

export function focusMatch(focusSim: number, designQuality: number): number {
  return clamp(focusSim * (BALANCE.focusBase + BALANCE.focusExec * designQuality));
}

export function polishScore(problems: Record<ProblemId, number>): number {
  let s = 0;
  for (const v of Object.values(problems)) s += v;
  return clamp(1 - s * BALANCE.problemWeight);
}

function weightsFor(r: Reviewer): Record<ReviewInput, number> {
  const w = {} as Record<ReviewInput, number>;
  let sum = 0;
  for (const k of Object.keys(BASE_WEIGHTS) as ReviewInput[]) {
    w[k] = Math.max(0, BASE_WEIGHTS[k] + (r.weights[k] ?? 0));
    sum += w[k];
  }
  for (const k of Object.keys(w) as ReviewInput[]) w[k] /= sum;
  return w;
}

function tasteOf(r: Reviewer, ctx: ReviewContext): { points: number; why: string } {
  const d = ctx.design;
  const c = ctx.compiled;
  switch (r.id) {
    case "table_talk": {
      const v = clamp((ctx.trend.novel ? 0.8 : 0.1) - 0.3 * ctx.trend.copies, -0.8, 0.8);
      return { points: v, why: ctx.trend.novel ? "Loves that nobody has made this combo before." : ctx.trend.copies > 0 ? `Has seen ${ctx.trend.copies} games like this lately.` : "Nothing new, nothing copied." };
    }
    case "heavy_box": {
      const depth = clamp((d.focus.depth - 2) / 3, -1, 1);
      const luck = clamp((0.4 - c.luck) / 0.4, -1, 1);
      return { points: 0.4 * depth + 0.4 * luck, why: luck < 0 ? "Hates how much the dice decide." : "Likes that skill wins." };
    }
    case "family_night": {
      const easy = 0.4 * clamp((d.focus.access - 2) / 2, -1, 1) + 0.4 * clamp((0.3 - c.complexity) / 0.3, -1, 1);
      const long = c.length > 60 ? -0.8 * clamp((c.length - 60) / 60) : 0;
      return { points: clamp(easy + long, -0.8, 0.8), why: long < 0 ? `About ${Math.round(c.length)} minutes: too long for a school night.` : d.focus.access >= 3 ? "Easy to learn." : "A bit hard to learn." };
    }
    case "cardboard_critic": {
      const v = 0.8 * clamp((ctx.value.ratio - 1) / 0.5, -1, 1);
      return { points: v, why: v >= 0 ? "Plenty of game for the money." : "The price is higher than the box feels worth." };
    }
    case "pixel_meeple": {
      const art = ctx.artQuality * (0.6 + 0.1 * d.focus.art);
      const v = 0.8 * clamp((art - 0.45) / 0.3, -1, 1);
      return { points: v, why: v >= 0 ? "A cover that pops." : "A bland cover." };
    }
    case "dungeon_ledger": {
      const lore = ctx.writingQuality * (0.5 + 0.1 * d.focus.immersion);
      const v = 0.8 * clamp((lore - 0.45) / 0.3, -1, 1) * (d.format === "rpg_book" ? 1 : 0.6);
      return { points: v, why: v >= 0 ? "Rich lore." : "Thin lore." };
    }
    default:
      return { points: 0, why: "" };
  }
}

function fitWhy(ctx: ReviewContext): string {
  const d = ctx.design;
  const f = ctx.fit;
  const m = C.mechanic[d.mechanic].name;
  const t = C.theme[d.theme].name;
  const a = C.audience[d.audience].name;
  const word = (n: number) => (n >= 5 ? "love" : n >= 4 ? "like" : n >= 3 ? "are fine with" : n >= 2 ? "aren't keen on" : "dislike");
  const pair = f.mt >= 4 ? `${m} and ${t} go well together` : f.mt >= 3 ? `${m} and ${t} work together` : `${m} and ${t} don't really fit`;
  const likes = word(f.ma) === word(f.ta) ? `${a} players ${word(f.ma)} both` : `${a} players ${word(f.ma)} ${m.toLowerCase()} ${(f.ma >= 3) === (f.ta >= 3) ? "and" : "but"} ${word(f.ta)} ${t.toLowerCase()}`;
  const parts = [`${pair}. ${likes}`];
  if (!f.formatOk) parts.push(`${m} doesn't suit a ${C.format[d.format].name.toLowerCase()}`);
  if (f.playersFactor < 1) parts.push(`${d.players[0]}–${d.players[1]} players is wrong for ${a}`);
  if (f.timeFactor < 1) parts.push(`${d.playTime} minutes is long for ${a} players`);
  if (f.perfect) parts.push(`A perfect combo: ${f.perfect}!`);
  return parts.join(". ") + ".";
}

/** The slider furthest from the hidden ideal: what reviewers hint at ("more Accessibility"). */
export function focusHint(d: Design): { slider: SliderId; gap: number } {
  const ideal = idealFocus(d.mechanic, d.audience);
  let worst: SliderId = SLIDERS[0];
  let worstGap = 0;
  for (const k of SLIDERS) {
    const gap = ideal[k] - d.focus[k];
    if (Math.abs(gap) > Math.abs(worstGap)) {
      worst = k;
      worstGap = gap;
    }
  }
  return { slider: worst, gap: worstGap };
}

function focusWhy(ctx: ReviewContext): string {
  const { slider: worst, gap: worstGap } = focusHint(ctx.design);
  const name = SLIDER_NAMES[worst];
  const a = C.audience[ctx.design.audience].name;
  const fit = ctx.focusSim >= 0.8 ? `Just what ${a} players want` : ctx.focusSim >= 0.55 ? `Close to what ${a} players want` : `Not what ${a} players want`;
  const hint = Math.abs(worstGap) >= 1 ? ` Try ${worstGap > 0 ? "more" : "less"} ${name}.` : "";
  const rough = ctx.designQuality < 0.4 ? " The design feels rough." : "";
  return `${fit}.${hint}${rough}`;
}

const SLIDER_NAMES: Record<string, string> = {
  depth: "Depth", access: "Accessibility", immersion: "Theme immersion", interaction: "Interaction", replay: "Replayability",
  art: "Art", components: "Components", speed: "Speed",
};

function polishWhy(problems: Record<ProblemId, number>): string {
  const left = problemList.filter((p) => problems[p.id] >= 0.15).sort((a, b) => problems[b.id] - problems[a.id]);
  if (!left.length) return "No problems left.";
  return `Still there: ${left.map((p) => `${p.name.toLowerCase()} (${problems[p.id] >= 0.6 ? "bad" : problems[p.id] >= 0.3 ? "noticeable" : "minor"})`).join(", ")}.`;
}

function valueWhy(ctx: ReviewContext): string {
  const v = ctx.value;
  return `Feels worth ${money(v.fair)}; it costs ${money(ctx.design.price)}.${v.overCeiling ? ` More than ${C.audience[ctx.design.audience].name} players pay.` : ""}`;
}

function trendWhy(t: TrendDetail, d: Design): string {
  const theme = C.theme[d.theme].name;
  const heat = t.themeHeat >= 0.6 ? "hot" : t.themeHeat >= 0.25 ? "warm" : "quiet";
  const bits = [`${theme} is ${heat} right now`];
  if (t.saturation > 0.05) bits.push("lots of games use this theme this year");
  if (t.copies > 0) bits.push(`${t.copies} similar game${t.copies > 1 ? "s" : ""} came out recently`);
  if (t.novel) bits.push("nobody has made this combo before");
  return bits.join(", and ") + ".";
}

const tenths = (x: number) => Math.round(x * 10);

export function reviewGame(ctx: ReviewContext): ReviewSet {
  const F = ctx.fit.F;
  const M = focusMatch(ctx.focusSim, ctx.designQuality);
  const P = polishScore(ctx.problems);
  const V = ctx.value.V;
  const T = ctx.trend.T;
  const inputs: Record<ReviewInput, number> = { fit: F, focus: M, polish: P, value: V, trend: T };
  const whys: Record<ReviewInput, string> = {
    fit: fitWhy(ctx), focus: focusWhy(ctx), polish: polishWhy(ctx.problems), value: valueWhy(ctx), trend: trendWhy(ctx.trend, ctx.design),
  };
  const labels: Record<ReviewInput, string> = { fit: "Fit", focus: "Focus", polish: "Polish", value: "Value for money", trend: "Timing" };
  const list = activeReviewers(ctx.design, ctx.week);
  let preHypeSum = 0;
  const reviews: Review[] = [];
  // hype gap uses the plain formula quality (before tastes) so all reviewers agree on it
  const plain = 10 * (0.3 * F + 0.25 * M + 0.2 * P + 0.15 * V + 0.1 * T);
  const H = clamp((ctx.hype - plain / 10) * 4, 0, 2);
  for (const r of list) {
    const w = weightsFor(r);
    const lines: BreakdownLine[] = [];
    for (const k of Object.keys(BASE_WEIGHTS) as ReviewInput[]) {
      lines.push({ key: k, label: labels[k], input: inputs[k], weight: w[k], points: tenths(10 * w[k] * inputs[k]) / 10, why: whys[k] });
    }
    const taste = tasteOf(r, ctx);
    lines.push({ key: "taste", label: `${r.name}'s taste`, points: tenths(clamp(taste.points, -0.8, 0.8)) / 10, why: taste.why || `Loves ${r.loves.toLowerCase()}, hates ${r.hates.toLowerCase()}.` });
    if (H > 0.05) lines.push({ key: "hype", label: "Overhyped", input: H / 2, points: -tenths(H) / 10, why: "The marketing promised more than the game delivers." });
    // the bar rises: critics have seen more every year, and only something new gets the old shine back
    const bar = raisedBar(ctx.week);
    if (bar > 0.05) lines.push({ key: "bar", label: "Seen it before", points: -bar, why: `Critics in ${yearOfWeek(ctx.week)} have played a lot of games: the bar is higher than it was.` });
    if (bar > 0.05 && ctx.trend.novel) lines.push({ key: "novel", label: "Something new", points: Math.min(bar, 0.3), why: "Nobody has made this pairing before." });
    let total = lines.reduce((s, l) => s + tenths(l.points), 0);
    preHypeSum += lines.filter((l) => l.key !== "hype").reduce((s, l) => s + tenths(l.points), 0) / 10;
    if (total < 0 || total > 100) {
      const adj = total < 0 ? -total : 100 - total;
      lines.push({ key: "limit", label: "Scores run 0 to 10", points: adj / 10, why: "Kept inside the scale." });
      total += adj;
    }
    reviews.push({ reviewer: r.id, score: total / 10, lines, quote: quoteFor(r, ctx, total / 10, lines) });
  }
  const avg = reviews.length ? reviews.reduce((s, r) => s + r.score, 0) / reviews.length : 0;
  return {
    reviews,
    score: Math.round(avg * 10) / 10,
    quality: clamp(preHypeSum / Math.max(1, list.length) / 10),
    inputs: { F, M, P, V, T, H },
  };
}

function quoteFor(r: Reviewer, ctx: ReviewContext, score: number, lines: BreakdownLine[] = []): string {
  const fill = (t: string) => t.replace("{mech}", C.mechanic[ctx.design.mechanic].name.toLowerCase()).replace("{theme}", C.theme[ctx.design.theme].name.toLowerCase());
  const q = r.quotes;
  if (q) {
    const h = hashString(ctx.design.name + r.id);
    const pick = (list: string[]) => list[h % list.length];
    if (ctx.followUp) return fill(pick(score >= 6.5 ? q.sequelGood : q.sequelBad));
    // a middling or poor review talks about what let the game down
    const weakest = [...lines].filter((l) => l.weight && l.weight > 0 && l.input !== undefined).sort((a, b) => (a.input ?? 1) - (b.input ?? 1))[0];
    if (score < 6.5 && weakest && (weakest.input ?? 1) < 0.5 && q.weak[weakest.key] && h % 3 !== 0) return fill(q.weak[weakest.key]);
    return fill(pick(score >= 8.3 ? q.great : score >= 6.8 ? q.good : score >= 5.2 ? q.mixed : q.bad));
  }
  // a follow-up is never "properly new": the novelty critic talks about the return instead
  if (ctx.followUp && (r.taste.novelty ?? 0) > 0) return score >= 7 ? `A welcome return to the world of ${C.theme[ctx.design.theme].name.toLowerCase()}.` : "More of the same, and it shows.";
  const i = score >= 7 ? (hashString(ctx.design.name + r.id) % 2 === 0 ? 0 : 2) : score < 5 ? 1 : 2;
  return (r.voice[i] ?? r.voice[0])
    .replace("{mech}", C.mechanic[ctx.design.mechanic].name.toLowerCase())
    .replace("{theme}", C.theme[ctx.design.theme].name.toLowerCase());
}

/** How much higher critics' expectations are this year than in the 1970s (points off every review, up to 0.5). */
export function raisedBar(week: number): number {
  return Math.round(Math.min(0.4, Math.max(0, (yearOfWeek(week) - 1980) * 0.007)) * 10) / 10;
}

function yearOfWeek(week: number): number {
  return 1965 + Math.floor(week / 52);
}
